import { useEffect, useState } from 'react';
import { ChevronDown, Download, Flag, HeartPulse, Timer, Trophy } from 'lucide-react';
import { SLEEP_CHOICES, SORE_AREAS, checkinHint } from './session';
import { FONT_BODY, FONT_MONO } from './theme';
import { Button, Card, Input } from './ui';

const chipStyle = (t, on) => ({
  minWidth: 40, height: 40, padding: '0 10px', borderRadius: 10, cursor: 'pointer', flexShrink: 0,
  background: on ? t.accentSoft : t.surfaceAlt, border: `1px solid ${on ? t.accentBorder : t.cardBorder}`,
  color: on ? t.accent : t.text, fontFamily: FONT_MONO, fontSize: 13, fontWeight: 700, touchAction: 'manipulation',
});

function ChipRow({ t, label, choices, value, onPick, text = v => String(v) }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: t.textFaint, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {choices.map(c => (
          // Tapping the chosen value again clears it.
          <button key={c} type="button" onClick={() => onPick(value === c ? null : c)} style={chipStyle(t, value === c)}>
            {text(c)}
          </button>
        ))}
      </div>
    </div>
  );
}

/* Optional readiness check-in (recovery[date]): raw values only, no score. */
export function CheckinCard({ t, checkin, dateLabel, onChange, defaultOpen = false }) {
  const c = checkin || {};
  const filled = ['sleepH', 'energy', 'soreness'].filter(k => typeof c[k] === 'number');
  const [open, setOpen] = useState(defaultOpen);
  const [areasOpen, setAreasOpen] = useState(false);
  const hint = checkinHint(c);
  const summary = filled.length
    ? [typeof c.sleepH === 'number' && `${c.sleepH === 5 ? '≤5' : c.sleepH === 9 ? '9+' : c.sleepH}h sleep`,
      typeof c.energy === 'number' && `energy ${c.energy}/5`,
      typeof c.soreness === 'number' && `sore ${c.soreness}/5`].filter(Boolean).join(' · ')
    : 'Optional · sleep, energy, soreness';
  const areas = Array.isArray(c.soreAreas) ? c.soreAreas : [];
  return (
    <Card t={t} padding={0}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} style={{
        width: '100%', minHeight: 48, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10,
        background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: FONT_BODY,
      }}>
        <HeartPulse size={16} color={hint ? '#f59e0b' : t.textFaint} style={{ flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: t.text }}>Check-in · {dateLabel}</span>
          <span style={{ display: 'block', fontSize: 11, color: t.textMuted, marginTop: 1 }}>{summary}</span>
        </span>
        <ChevronDown size={16} color={t.textFaint} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>
      {hint && !open && <div style={{ padding: '0 14px 10px 40px', fontSize: 12, color: '#f59e0b' }}>{hint}</div>}
      {open && (
        <div style={{ padding: '4px 14px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <ChipRow t={t} label="Sleep (hours)" choices={SLEEP_CHOICES} value={c.sleepH}
            text={v => (v === 5 ? '≤5' : v === 9 ? '9+' : String(v))} onPick={v => onChange({ sleepH: v })} />
          <ChipRow t={t} label="Energy (1 drained · 5 great)" choices={[1, 2, 3, 4, 5]} value={c.energy}
            onPick={v => onChange({ energy: v })} />
          <ChipRow t={t} label="Soreness (1 none · 5 very)" choices={[1, 2, 3, 4, 5]} value={c.soreness}
            onPick={v => onChange({ soreness: v })} />
          <div>
            <button type="button" onClick={() => setAreasOpen(o => !o)} style={{
              background: 'transparent', border: 'none', padding: 0, minHeight: 32, cursor: 'pointer',
              color: t.textMuted, fontSize: 12, fontFamily: FONT_BODY, fontWeight: 600,
            }}>
              Sore areas{areas.length ? `: ${areas.join(', ')}` : ' (optional)'} {areasOpen ? '▴' : '▾'}
            </button>
            {areasOpen && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                {SORE_AREAS.map(a => {
                  const on = areas.includes(a);
                  return (
                    <button key={a} type="button" style={{ ...chipStyle(t, on), fontFamily: FONT_BODY, fontSize: 12 }}
                      onClick={() => onChange({ soreAreas: on ? areas.filter(x => x !== a) : [...areas, a] })}>
                      {a}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {hint && <div style={{ fontSize: 12, color: '#f59e0b' }}>{hint}</div>}
        </div>
      )}
    </Card>
  );
}

const clockText = ms => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = n => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};

/* Live workout clock (from the first ticked set) and a Rest button that
 * starts the rest timer by hand. Only on the live day. */
export function WorkoutBar({ t, startedAt, clock = true, restSec, restActive, onRest }) {
  const [now, setNow] = useState(() => Date.now());
  const running = clock && typeof startedAt === 'number';
  useEffect(() => {
    if (!running) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [running]);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 4px' }}>
      <Timer size={16} color={running ? t.accent : t.textFaint} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0, fontSize: running ? 18 : 13, fontFamily: running ? FONT_MONO : FONT_BODY,
        fontWeight: running ? 700 : 400, color: running ? t.text : t.textMuted, fontVariantNumeric: 'tabular-nums' }}>
        {running ? clockText(now - startedAt) : clock ? 'Workout clock starts at your first set' : 'Rest timer'}
      </span>
      {!restActive && (
        <Button t={t} size="sm" onClick={onRest} style={{ minHeight: 40, flexShrink: 0 }}>
          Rest {Math.floor(restSec / 60)}:{String(restSec % 60).padStart(2, '0')}
        </Button>
      )}
    </div>
  );
}

const fmtVolume = v =>`${Math.round(v).toLocaleString('en-GB')} kg`;

/* End-of-session card: what got done, against last time, and how it felt. */
export function FinishCard({
  t, open, onToggle, minutes, progress, volume, lastSame, prs, session, onSession, onExport,
}) {
  const [note, setNote] = useState(null); // draft while typing; saved on blur
  const volDelta = lastSame && lastSame.volume > 0 ? Math.round(((volume - lastSame.volume) / lastSame.volume) * 100) : null;
  const rpe = session?.rpe;
  return (
    <Card t={t} padding={0}>
      <button type="button" onClick={onToggle} aria-expanded={open} style={{
        width: '100%', minHeight: 52, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
        background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: FONT_BODY,
      }}>
        <Flag size={16} color={progress.pct === 100 ? t.accent : t.textFaint} style={{ flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: t.text }}>Session summary</span>
          <span style={{ display: 'block', fontSize: 11, color: t.textMuted, marginTop: 1, fontFamily: FONT_MONO }}>
            {progress.done}/{progress.total} planned sets · {fmtVolume(volume)}
            {minutes ? ` · ${minutes} min` : ''}{rpe ? ` · RPE ${rpe}` : ''}
          </span>
        </span>
        <ChevronDown size={16} color={t.textFaint} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>
      {open && (
        <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[
              ['Duration', minutes ? `${minutes} min` : '—'],
              ['Planned sets', `${progress.pct}%`],
              ['Volume', volDelta === null ? fmtVolume(volume) : `${volDelta >= 0 ? '+' : ''}${volDelta}%`],
            ].map(([label, value]) => (
              <div key={label} style={{ padding: '8px 10px', borderRadius: 10, background: t.surfaceAlt, border: `1px solid ${t.cardBorder}` }}>
                <div style={{ fontSize: 10, color: t.textFaint, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                <div style={{ fontSize: 15, fontFamily: FONT_MONO, fontWeight: 700, color: t.text, marginTop: 2 }}>{value}</div>
              </div>
            ))}
          </div>
          {lastSame && (
            <div style={{ fontSize: 11, color: t.textMuted, marginTop: -6 }}>
              Volume vs the last time you did this day ({new Date(lastSame.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, {fmtVolume(lastSame.volume)}).
            </div>
          )}
          {progress.extras.length > 0 && (
            <div style={{ fontSize: 12, color: t.textMuted }}>
              Extra: {progress.extras.map(x => `${x.name} ${x.sets} set${x.sets === 1 ? '' : 's'}`).join(' · ')}
            </div>
          )}
          {prs.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {prs.map(p => (
                <div key={`${p.exercise}-${p.type}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: t.text }}>
                  <Trophy size={14} color="#f59e0b" style={{ flexShrink: 0 }} />
                  <span style={{ fontWeight: 600 }}>{p.exercise}</span>
                  <span style={{ fontFamily: FONT_MONO, color: t.textMuted }}>{p.set.weight}kg × {p.set.reps}</span>
                </div>
              ))}
            </div>
          )}
          <div>
            <div style={{ fontSize: 11, color: t.textFaint, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
              How hard was it? (session RPE)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                <button key={n} type="button" onClick={() => onSession({ rpe: rpe === n ? null : n })}
                  style={{ ...chipStyle(t, rpe === n), minWidth: 0 }}>{n}</button>
              ))}
            </div>
          </div>
          <Input t={t} value={note ?? session?.note ?? ''} maxLength={200} placeholder="Session note (e.g. shoulder twinge on bench)"
            onChange={e => setNote(e.target.value)}
            onBlur={() => { if (note !== null) { onSession({ note: note.trim() || null }); setNote(null); } }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button t={t} size="sm" variant="ghost" onClick={onExport} style={{ minHeight: 40 }}>
              <Download size={13} /> Export JSON
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
